import cn from 'classnames'
import { useState } from 'react'
import { useUpdateEffect } from 'react-use'

import styles from './FeedbackStatusSelect.module.scss'

import Select from '~elements/SelectV2'
import { FeedbackPostStatus } from '~generated/types'
import DoneIcon from '~icons/illustrations/done-xxs.svg'
import InProgressIcon from '~icons/illustrations/in_progress-xxs.svg'
import InReviewIcon from '~icons/illustrations/in_review-xxs.svg'
import PlannedIcon from '~icons/illustrations/planned-xxs.svg'
import RefusedIcon from '~icons/illustrations/refused-xxs.svg'
import Menu from '~lib/ui-kit/Menu/Menu'
import Option from '~lib/ui-kit/Option'
import Text from '~shared/ui/Text'

interface FeedbackStatusSelectProps {
  value?: FeedbackPostStatus
  defaultValue?: FeedbackPostStatus
  onChange?: (value: FeedbackPostStatus) => void
  onClick?: (ev: React.MouseEvent<HTMLElement>) => void
  size?: 'sm' | 'md'
  readOnly?: boolean
  disabled?: boolean
  className?: string
}

const options = [
  {
    Icon: InReviewIcon,
    value: FeedbackPostStatus.IN_REVIEW,
    label: 'New Idea',
  },
  {
    Icon: PlannedIcon,
    value: FeedbackPostStatus.PLANNED,
    label: 'Planned',
  },
  {
    Icon: InProgressIcon,
    value: FeedbackPostStatus.IN_PROGRESS,
    label: 'In Progress',
  },
  {
    Icon: DoneIcon,
    value: FeedbackPostStatus.DONE,
    label: 'Done',
  },
  {
    Icon: RefusedIcon,
    value: FeedbackPostStatus.REFUSED,
    label: 'Refused',
  },
]

// TODO - refactor
function FeedbackStatusSelect({
  value,
  defaultValue,
  onChange,
  size = 'md',
  readOnly,
  disabled,
  className,
  ...props
}: FeedbackStatusSelectProps) {
  const [valueState, setValueState] = useState(() => defaultValue || value)

  useUpdateEffect(() => {
    if (typeof value !== 'undefined') {
      setValueState(value)
    }
  }, [value])

  const handleChange = (value: FeedbackPostStatus) => {
    if (typeof value === 'undefined') {
      setValueState(value)
    }

    onChange?.(value)
  }

  const [buttonRef, setButtonRef] = useState<HTMLButtonElement | null>(null)

  if (size === 'sm') {
    const option = options.find((option) => {
      return option.value === value || option.value === defaultValue
    })

    const Icon = option?.Icon || InReviewIcon

    return (
      <>
        <button
          ref={setButtonRef}
          className={cn(styles.root, className)}
          type="button"
          disabled={disabled || readOnly}
          {...props}
        >
          <Icon />

          {readOnly && (
            <Text variant="secondary" textType="body-sm" type="caption" className="ml-1">
              {options.find((option) => option.value === valueState)?.label}
            </Text>
          )}
        </button>

        <Menu anchorEl={buttonRef}>
          {options.map(({ value, label, Icon }) => {
            return (
              <Option
                key={value}
                icon={<Icon />}
                value={value}
                selected={valueState === value}
                onClick={() => handleChange(value)}
              >
                {label}
              </Option>
            )
          })}
        </Menu>
      </>
    )
  }

  return (
    <Select
      variant="dark"
      label="Status"
      size="sm"
      bordered
      value={valueState}
      onChange={onChange}
      disabled={disabled}
      readOnly={readOnly}
      icon={null}
      className={className}
      {...props}
    >
      {options.map(({ value, label, Icon }) => {
        return (
          <Option key={value} icon={<Icon />} value={value}>
            {label}
          </Option>
        )
      })}
    </Select>
  )
}

export default FeedbackStatusSelect
